import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AccessRequest from "@/pages/AccessRequest";
import Admin from "@/pages/Admin";
import Dashboard from "@/pages/Dashboard";
import PropertyForm from "@/pages/PropertyForm";
import DestinationAnalysis from "@/pages/DestinationAnalysis";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminMarketData from "@/pages/AdminMarketData";
import DataImport from "@/pages/DataImport";
import CompetitorDetail from "@/pages/CompetitorDetail";
import MyProperty from "@/pages/MyProperty";
import QuickQuote from "@/pages/QuickQuote";
import Competitors from "@/pages/Competitors";
import Rooms from "@/pages/Rooms";
import Market from "@/pages/Market";
import PeriodAnalysis from "@/pages/PeriodAnalysis";
import Costs from "@/pages/Costs";
import VideoGuides from "@/pages/VideoGuides";
import Subscription from "@/pages/Subscription";
import PaymentSuccess from "@/pages/PaymentSuccess";
import DecisionHistory from "@/pages/DecisionHistory";
import CompleteSubscription from "@/pages/CompleteSubscription";
import LegalPage from "@/pages/LegalPage";
import CookieBanner from "@/components/CookieBanner";
import LegalGate from "@/components/LegalGate";

function Protected({ children }) {
  const { user } = useApp();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Routed() {
  const { user } = useApp();
  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Auth />} />
      <Route path="/request-access" element={user ? <Navigate to="/dashboard" replace /> : <AccessRequest />} />
      <Route path="/complete-subscription" element={<CompleteSubscription />} />
      <Route path="/privacy" element={<LegalPage docType="privacy" />} />
      <Route path="/cookie-policy" element={<LegalPage docType="cookie" />} />
      <Route path="/terms" element={<LegalPage docType="terms" />} />
      <Route path="/dpa" element={<LegalPage docType="dpa" />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/destination" element={<Protected><DestinationAnalysis /></Protected>} />
      <Route path="/import" element={<Protected><DataImport /></Protected>} />
      <Route path="/competitor" element={<Protected><CompetitorDetail /></Protected>} />
      <Route path="/my-property" element={<Protected><MyProperty /></Protected>} />
      <Route path="/quick-quote" element={<Protected><QuickQuote /></Protected>} />
      <Route path="/competitors" element={<Protected><Competitors /></Protected>} />
      <Route path="/competitors/period" element={<Protected><PeriodAnalysis /></Protected>} />
      <Route path="/market" element={<Protected><Market /></Protected>} />
      <Route path="/decisions" element={<Protected><DecisionHistory /></Protected>} />
      <Route path="/rooms" element={<Protected><Rooms /></Protected>} />
      <Route path="/costs" element={<Protected><Costs /></Protected>} />
      <Route path="/guide" element={<Protected><VideoGuides /></Protected>} />
      <Route path="/subscription" element={<Protected><Subscription /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="/admin/market-data" element={<Protected><AdminMarketData /></Protected>} />
      <Route path="/properties/new" element={<Protected><PropertyForm /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <Routed />
          <CookieBanner />
          <LegalGate />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}
